/**
 * Generic Selectors for Geometry Editing State
 *
 * Abstract over which artifact form owns the geometry-editing session by
 * iterating registered artifacts (`src/artifacts/registry.ts`). Adding a
 * new artifact (`ArtifactDefinition`) automatically lights these selectors
 * up — no edit here required.
 *
 * @module store/selectors/geometryEditingSelectors
 */

import { getAllArtifacts } from '../../artifacts/registry';
import type { GeoMarkupStore } from '../index';
import { TaskFormMode } from '../../types';
import type { Coordinates } from '../../types';

function asTaskFormMode(mode: 'create' | 'edit' | null): TaskFormMode | null {
  if (mode === 'create') return TaskFormMode.Create;
  if (mode === 'edit') return TaskFormMode.Edit;
  return null;
}

function findOpenFormSlice(state: GeoMarkupStore) {
  for (const artifact of getAllArtifacts()) {
    const slice = artifact.getSlice(state);
    if (slice.formIsOpen) return slice;
  }
  return null;
}

export const selectIsGeometryEditingActive = (state: GeoMarkupStore): boolean =>
  findOpenFormSlice(state) !== null;

export const selectGeometryEditingMode = (
  state: GeoMarkupStore
): TaskFormMode | null => {
  const slice = findOpenFormSlice(state);
  return slice ? asTaskFormMode(slice.formMode) : null;
};

export const selectIsGeometryCreateMode = (state: GeoMarkupStore): boolean => {
  return selectGeometryEditingMode(state) === TaskFormMode.Create;
};

export const selectIsGeometryEditMode = (state: GeoMarkupStore): boolean => {
  return selectGeometryEditingMode(state) === TaskFormMode.Edit;
};

export const selectEditingEntityId = (state: GeoMarkupStore): string | null => {
  const slice = findOpenFormSlice(state);
  return slice && slice.formMode === 'edit' ? slice.selectedId : null;
};

export const selectGeometryEditingAnchorPoint = (
  state: GeoMarkupStore
): Coordinates | null => {
  const slice = findOpenFormSlice(state);
  return slice ? slice.formAnchorPoint : null;
};
